<template>
  <ts-modal
    ref="modal"
    modal-title="Cancel participation"
    modal-class="cancel-registration-modal"
    :keyboard="false"
  >
    <template #modal-content>
      <template v-if="showForm">
        <alert
          type="danger"
          :message="errorMessage"
          @dismiss="clearError"
        />
        <alert
          type="warning"
          :dismissable="false"
          message="You are about to cancel participation, are you sure?"
        />
        <div
          v-if="cancellationReasons.length"
          class="mb-3"
        >
          <label class="form-label required">Cancellation reason</label>
          <div
            v-for="reason of cancellationReasons"
            :key="reason.id"
          >
            <div class="form-check">
              <input
                :id="`${idPrefix}-reason-${reason.id}`"
                v-model="selectedReason"
                :value="reason"
                type="radio"
                name="reason_id"
                class="form-check-input"
              >
              <label
                class="form-check-label"
                :for="`${idPrefix}-reason-${reason.id}`"
              >
                {{ reason.title }}
              </label>
            </div>
          </div>
        </div>
        <div class="mb-3">
          <label
            class="form-label"
            :class="{required: requireCancellationComment }"
            :for="`${idPrefix}-comment`"
          >
            Comment
          </label>
          <textarea
            :id="`${idPrefix}-comment`"
            v-model="cancellationReason"
            class="form-control"
          />
        </div>
      </template>
      <loading-indicator
        v-if="isLoading"
        message="Please wait while action is performed ..."
      />
    </template>
    <template #modal-footer>
      <button
        type="button"
        class="btn btn-sm btn-success"
        :disabled="isLoading"
        data-bs-dismiss="modal"
      >
        Close
      </button>
      <button
        type="button"
        :disabled="isLoading"
        class="btn btn-sm btn-danger"
        @click.prevent="cancelRegistration"
      >
        Cancel participation
      </button>
    </template>
  </ts-modal>
</template>

<script setup lang="ts">
import {computed, ref, useTemplateRef, type PropType} from 'vue'
import TsModal from './ts-modal.vue'
import Alert from './alert.vue'
import LoadingIndicator from './loading-indicator.vue'
import axios from 'axios'
import Rails from '@rails/ujs'
import {isNonEmptyString} from '../js-utils'
import type { CancellationReason } from './types'

const props = defineProps({
  cancelUrl: {default: null, type: String as PropType<string | null>},
  cancellationReasons: {type: Array as () => CancellationReason[], default: (): CancellationReason[] => []}
})

const emit = defineEmits<{
  (e: "participation-canceled", reasonId?: string, comment?: string): void
}>()

const modal = useTemplateRef<InstanceType<typeof TsModal>>("modal")

const errorMessage = ref<string | null>(null)
const isLoading = ref<boolean>(false)
const cancellationReason = ref<string>("")
const selectedReason = ref<CancellationReason | null>(null)
const idPrefix = ref<string>(`mdl-${new Date().getTime()}`)

const showForm = computed((): boolean => {
  return !isLoading.value
})
const requireCancellationComment = computed((): boolean => {
  return selectedReason.value?.requireComment || false
})
const clearError = (): void => {
  isLoading.value = false
  errorMessage.value = null
}

const showModal = (): void => {
  cancellationReason.value = ''
  clearError()
  modal.value?.show()
}

const hideModal = (): void => {
  modal.value?.hide()
}

const cancelRegistration = (): void => {
  if (isLoading.value) {
    return
  }

  if (!isNonEmptyString(props.cancelUrl)) {
    emit('participation-canceled', selectedReason.value?.id, cancellationReason.value)
    return
  }

  isLoading.value = true

  const formData = new FormData()
  formData.append('reason', cancellationReason.value)
  if (selectedReason.value) {
    formData.append('reason_id', selectedReason.value.id)
  }
  formData.append('_method', 'DELETE')

  axios.post(props.cancelUrl, formData, {
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'X-Requested-With': 'XMLHttpRequest',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'X-CSRF-Token': Rails.csrfToken()
    }
  })
    .then((response) => {
      if (typeof response.data.message === 'string') {
        if (response.data.success) {
          emit('participation-canceled')
        } else {
          errorMessage.value = response.data.message
        }
      } else {
        errorMessage.value = 'Unknown response from server'
      }
      isLoading.value = false
    })
    .catch((error) => {
      errorMessage.value = error.message
      isLoading.value = false
    })
}

defineExpose({
  showModal,
  hideModal,
})
</script>
